import React, { useState, useMemo } from 'react';

const TrendsFirstBasketReport = ({ data, teams, loading, oddsData, onPlayerClick, isPastYear = false, sport = 'nba', currentTeamByPlayerId: externalRosterMap = null }) => {
  const [currentStat, setCurrentStat] = useState('player_first_fg');

  // Prefer the externally-supplied roster (from /today payload — complete current roster).
  // Fall back to deriving from odds data, which is market-scoped and may miss players.
  const oddsRosterMap = useMemo(() => {
    const map = new Map();
    if (!oddsData?.players) return map;
    oddsData.players.forEach(p => {
      const pid = String(p.playerId);
      const team = p.team || p.teamId;
      if (pid && team && !map.has(pid)) {
        map.set(pid, team);
      }
    });
    return map;
  }, [oddsData]);
  const currentTeamByPlayerId = (externalRosterMap && externalRosterMap.size > 0) ? externalRosterMap : oddsRosterMap;

  // Parse teams from "MIA @ WSH" format
  const parseTeams = () => {
    if (!teams) return { away: null, home: null };
    const parts = teams.split('@').map(t => t.trim());
    return { away: parts[0] || null, home: parts[1] || null };
  };

  const { away: awayTeam, home: homeTeam } = parseTeams();

  // Extract players with odds from odds data
  const playersWithOdds = useMemo(() => {
    if (!oddsData?.players) return new Set();

    const playerIds = new Set();
    oddsData.players.forEach(player => {
      // Skip if not available
      if (player.isAvailable === false) return;
      if (player.playerId) {
        playerIds.add(String(player.playerId));
      }
    });
    return playerIds;
  }, [oddsData]);

  // Check if player has odds
  const playerHasOdds = (playerId) => {
    return playersWithOdds.has(String(playerId));
  };

  // Map stat keys to their display labels and API field names
  const statConfig = {
    'player_first_shot': { label: '1st Shot' },
    'player_first_fg': { label: '1st FG' },
    'player_first_points': { label: '1st Pts' },
    'player_first_team_shot': { label: 'Team Shot' },
    'player_first_team_fg': { label: 'Team FG' },
    'player_first_team_points': { label: 'Team Pts' },
    'tips': { label: 'Tips' }
  };

  // Helper to extract stat values from the nested API structure
  const getStatValue = (player, statKey, valueType) => {
    const statData = player[statKey];
    if (!statData?.season?.milestones?.['1']) return 0;
    const milestone = statData.season.milestones['1'];
    if (valueType === 'pct') return milestone.pct || 0;
    if (valueType === 'count') return milestone.hit || 0;
    return 0;
  };

  const getGamesPlayed = (player, statKey) => {
    const statData = player[statKey];
    return statData?.season?.gamesPlayed || 0;
  };

  const styles = {
    container: {
      width: '100%',
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      textAlign: 'center',
      marginBottom: '0.5rem'
    },
    title: {
      fontSize: '1.2em',
      fontWeight: '600',
      fontFamily: "'Oswald', sans-serif",
      margin: 0,
      textTransform: 'uppercase',
      color: '#ffc107'
    },
    toggleContainer: {
      display: 'flex',
      gap: '0.35rem',
      justifyContent: 'center',
      marginBottom: '0.5rem',
      flexWrap: 'wrap'
    },
    statToggle: {
      background: '#333',
      color: '#888',
      border: '1px solid #444',
      padding: '0.25rem 0.4rem',
      fontSize: '0.65rem',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      fontWeight: '600'
    },
    statToggleActive: {
      background: '#3498db',
      color: 'white',
      borderColor: '#3498db'
    },
    columnsContainer: {
      display: 'flex',
      gap: '0.5rem',
      flex: 1,
      overflow: 'hidden'
    },
    teamColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    teamHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.4rem',
      background: '#222',
      borderRadius: '0.25rem',
      marginBottom: '0.5rem'
    },
    teamLogo: {
      width: '24px',
      height: '24px',
      objectFit: 'contain'
    },
    teamName: {
      fontSize: '0.85rem',
      fontWeight: 'bold'
    },
    playersList: {
      flex: 1,
      overflowY: 'auto'
    },
    playerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem',
      borderBottom: '1px solid #333',
      background: '#1a1a1a',
      borderRadius: '0.35rem',
      marginBottom: '0.35rem'
    },
    rank: {
      width: '18px',
      fontSize: '0.75rem',
      color: '#888',
      textAlign: 'center',
      flexShrink: 0
    },
    headshot: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      objectFit: 'cover',
      flexShrink: 0
    },
    playerInfo: {
      flex: 1,
      minWidth: 0
    },
    playerName: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    statsLine: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      marginTop: '0.1rem'
    },
    hitRate: {
      fontSize: '0.8rem',
      fontWeight: 'bold',
      color: '#f33'
    },
    hitRateHigh: {
      color: '#4f4'
    },
    hitRateLow: {
      color: '#f44'
    },
    statCount: {
      fontSize: '0.65rem',
      color: '#888'
    },
    pctBarInline: {
      flex: 1,
      background: '#333',
      height: '4px',
      borderRadius: '2px',
      overflow: 'hidden'
    },
    pctBarFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #f33, #f93)',
      borderRadius: '2px'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      color: '#888'
    },
    noData: {
      textAlign: 'center',
      padding: '0.5rem',
      color: '#666',
      fontSize: '0.75rem'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading first basket data...</div>;
  }

  const playersData = data?.players || [];

  // Helper to calculate tip success rate (tips won / tips taken)
  const getTipSuccessRate = (player) => {
    const tipsWon = getStatValue(player, 'player_tip_winner', 'count');
    const tipsTaken = getStatValue(player, 'player_tip', 'count');
    if (tipsTaken === 0) return 0;
    return (tipsWon / tipsTaken) * 100;
  };

  // Helper to render a single player row
  const renderPlayerRow = (player, index, statKey, isTipMode = false) => {
    const pct = isTipMode
      ? getTipSuccessRate(player)
      : getStatValue(player, statKey, 'pct');
    const count = isTipMode
      ? getStatValue(player, 'player_tip_winner', 'count')
      : getStatValue(player, statKey, 'count');
    const gamesPlayed = isTipMode
      ? getStatValue(player, 'player_tip', 'count')
      : getGamesPlayed(player, statKey);

    const headshotUrl = player.playerId
      ? `https://a.espncdn.com/combiner/i?img=/i/headshots/${sport}/players/full/${player.playerId}.png&w=96&h=70&cb=1`
      : null;

    let rateStyle = styles.hitRate;
    if (isTipMode) {
      if (pct >= 60) rateStyle = { ...styles.hitRate, ...styles.hitRateHigh };
      else if (pct <= 40) rateStyle = { ...styles.hitRate, ...styles.hitRateLow };
    } else {
      if (pct >= 15) rateStyle = { ...styles.hitRate, ...styles.hitRateHigh };
      else if (pct <= 5) rateStyle = { ...styles.hitRate, ...styles.hitRateLow };
    }

    const hasOdds = playerHasOdds(player.playerId);
    const rowStyle = {
      ...styles.playerRow,
      ...(hasOdds ? {} : { opacity: 0.25 })
    };

    return (
      <div key={player.playerId || index} style={rowStyle}>
        <div style={styles.rank}>{index + 1}</div>
        {headshotUrl && (
          <img
            style={styles.headshot}
            src={headshotUrl}
            alt={player.playerName}
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <div style={styles.playerInfo}>
          <div style={{ ...styles.playerName, ...(onPlayerClick ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#6b7280' } : {}) }} onClick={onPlayerClick ? () => onPlayerClick(player.playerId, player.playerName, player.team) : undefined}>
            {player.playerName}
            {isPastYear && player.team && currentTeamByPlayerId.get(String(player.playerId)) && player.team !== currentTeamByPlayerId.get(String(player.playerId)) && (
              <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '4px' }}>({player.team})</span>
            )}
          </div>
          <div style={styles.statsLine}>
            <span style={rateStyle}>{pct.toFixed(1)}%</span>
            <span style={styles.statCount}>
              {isTipMode ? `${count}/${gamesPlayed}` : `${count}/${gamesPlayed}`}
            </span>
            <div style={styles.pctBarInline}>
              <div style={{ ...styles.pctBarFill, width: `${pct}%` }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render a team column
  const renderTeamColumn = (teamAbbrev, isTipMode = false) => {
    if (!teamAbbrev) {
      return <div style={styles.noData}>No team data</div>;
    }

    const teamPlayers = isPastYear
      ? playersData.filter(p => currentTeamByPlayerId.get(String(p.playerId)) === teamAbbrev)
      : playersData.filter(p => p.team === teamAbbrev);

    if (teamPlayers.length === 0) {
      return <div style={styles.noData}>No players for {teamAbbrev}</div>;
    }

    // Sort by percentage for players, by tips taken for tips
    const sorted = [...teamPlayers].sort((a, b) => {
      if (isTipMode) {
        return getStatValue(b, 'player_tip', 'count') - getStatValue(a, 'player_tip', 'count');
      }
      return getStatValue(b, currentStat, 'pct') - getStatValue(a, currentStat, 'pct');
    });

    // Filter to players with data
    const filterKey = isTipMode ? 'player_tip' : currentStat;
    const filtered = sorted.filter(p => getStatValue(p, filterKey, 'count') > 0);

    if (filtered.length === 0) {
      return <div style={styles.noData}>No data available</div>;
    }

    return filtered.slice(0, 10).map((player, index) =>
      renderPlayerRow(player, index, currentStat, isTipMode)
    );
  };

  const renderTwoColumnView = (isTipMode = false) => {
    return (
      <div style={styles.columnsContainer}>
        {/* Away Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${awayTeam?.toLowerCase()}.png`}
              alt={awayTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{awayTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(awayTeam, isTipMode)}
          </div>
        </div>

        {/* Home Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${homeTeam?.toLowerCase()}.png`}
              alt={homeTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{homeTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(homeTeam, isTipMode)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>FIRST BASKETS</h2>
      </div>

      {/* Stat Toggles */}
      <div style={styles.toggleContainer}>
        {Object.entries(statConfig).map(([stat, config]) => (
          <button
            key={stat}
            style={{
              ...styles.statToggle,
              ...(currentStat === stat ? styles.statToggleActive : {})
            }}
            onClick={() => setCurrentStat(stat)}
          >
            {config.label}
          </button>
        ))}
      </div>

      {/* Two Column Data Display */}
      {renderTwoColumnView(currentStat === 'tips')}
    </div>
  );
};

export default TrendsFirstBasketReport;
